import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { daysInEthiopianMonth, ethiopianMonthName, formatDualDate, fromEthiopian, toEthiopian } from '@dairy/domain';
import { isValidIsoDate } from '@dairy/shared';
import { useSession } from '@/auth/useSession';
import { Input, Select } from './ui';
import { cn } from '@/lib/cn';

/**
 * PL-12 date field: pick a date in either calendar; the value is always Gregorian "YYYY-MM-DD".
 * EC mode = day, month (13 names) and year selects with correct day limits.
 */
export function DateField({
  value,
  onChange,
  max,
  min,
  id,
  invalid,
  className,
}: {
  value: string;
  onChange: (iso: string) => void;
  max?: string;
  min?: string;
  id?: string;
  invalid?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const profile = useSession().profile;
  const locale = profile?.locale ?? 'am';
  const [mode, setMode] = useState<'EC' | 'GC'>(profile?.primaryCalendar ?? 'EC');
  useEffect(() => { setMode(profile?.primaryCalendar ?? 'EC'); }, [profile?.primaryCalendar]);
  const valid = isValidIsoDate(value);
  const eth = valid ? toEthiopian(value) : toEthiopian(max ?? new Date().toISOString().slice(0, 10));
  const maxEthYear = max ? toEthiopian(max).year : eth.year + 1;
  const years = Array.from({ length: 30 }, (_, i) => maxEthYear - i);

  const setEth = (patch: Partial<typeof eth>) => {
    const next = { ...eth, ...patch };
    next.day = Math.min(next.day, daysInEthiopianMonth(next.year, next.month));
    onChange(fromEthiopian(next));
  };

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex gap-2">
        {mode === 'GC' ? (
          <Input id={id} type="date" value={value} max={max} min={min} aria-invalid={invalid} onChange={(e) => onChange(e.target.value)} />
        ) : (
          <div className="grid flex-1 grid-cols-[4.5rem_1fr_5.5rem] gap-2">
            <Select id={id} aria-label={t('date.day')} value={eth.day} onChange={(e) => setEth({ day: Number(e.target.value) })} aria-invalid={invalid}>
              {Array.from({ length: daysInEthiopianMonth(eth.year, eth.month) }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
            <Select aria-label={t('date.month')} value={eth.month} onChange={(e) => setEth({ month: Number(e.target.value) })}>
              {Array.from({ length: 13 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {ethiopianMonthName(m, locale)}
                </option>
              ))}
            </Select>
            <Select aria-label={t('date.year')} value={eth.year} onChange={(e) => setEth({ year: Number(e.target.value) })}>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </div>
        )}
        <button
          type="button"
          onClick={() => setMode(mode === 'EC' ? 'GC' : 'EC')}
          className="h-12 shrink-0 rounded-xl border border-stone-300 bg-white px-3 text-sm font-semibold text-stone-800"
          aria-label={t('date.switchCalendar')}
        >
          {mode === 'EC' ? t('date.ec') : t('date.gc')}
        </button>
      </div>
      {valid ? <p className="text-sm text-stone-600">{formatDualDate(value, locale, profile?.primaryCalendar ?? 'EC')}</p> : null}
    </div>
  );
}
